import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  FaBook,
  FaCheck,
  FaShieldAlt,
  FaRocket,
  FaArrowRight,
  FaLock,
  FaLayerGroup,
} from "react-icons/fa";
import { AiFillSound } from "react-icons/ai";
import { HiSparkles, HiCheckBadge } from "react-icons/hi2";

/* ------------------------------------------------------------------ */
/*  Card.js — 100% Redesigned Human-Crafted 3-Column Ecosystem Grid   */
/* ------------------------------------------------------------------ */

const Card = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const prefersReducedMotion = useReducedMotion();

  const services = [
    {
      id: "publisher",
      tag: "Publisher Network",
      badge: "Guaranteed NET 50",
      icon: <FaBook />,
      title: "Publisher Ecosystem",
      subtitle: "First-Look Inventory & High-Yield Formats",
      description:
        "At Myad360, we understand that reliability and revenue matter most to publishers. We offer NET 50 payment terms with 100% on-time payouts so you get paid reliably every single month.",
      bullets: [
        "Skin & Gutter High-Impact Ads",
        "Floating & Footers Placements",
        "Next-Gen Native Ad Units",
        "Direct First-Look Monetization",
      ],
      highlight: "100% On-Time Payouts",
      cardStyle: {
        background: "linear-gradient(165deg, #FFFFFF 0%, #FFFBF5 100%)",
        borderColor: "#F0DCC8",
        borderRadius: "2.2rem 0.8rem 2.2rem 0.8rem",
        accent: "#D97706",
        accentBg: "#FEF3E5",
      },
    },
    {
      id: "advertiser",
      tag: "Advertiser Reach",
      badge: "Targeted Long-Tail",
      icon: <AiFillSound />,
      title: "Advertiser Network",
      subtitle: "Curated Traffic & Precision Audience Reach",
      description:
        "We specialize in delivering high-quality, targeted traffic through a handpicked network of long-tail websites vetted for audience quality, brand safety, and deep content engagement.",
      bullets: [
        "Handpicked Premium Long-Tail Sites",
        "Display, Mobile & Video Coverage",
        "Contextual Audience Alignment",
        "High-Engagement Return Metrics",
      ],
      highlight: "Curated Media Inventory",
      cardStyle: {
        background: "linear-gradient(165deg, #FFFFFF 0%, #FFF9F2 100%)",
        borderColor: "#F0DCC8",
        borderRadius: "0.8rem 2.2rem 0.8rem 2.2rem",
        accent: "#E8882D",
        accentBg: "#FEF3E5",
      },
    },
    {
      id: "quality",
      tag: "Brand Safety",
      badge: "Forensiq Pre-Bidded",
      icon: <FaShieldAlt />,
      title: "Quality & Security",
      subtitle: "24/7 Fraud & Malware Defense",
      description:
        "Every ad unit served across Myad360 undergoes real-time malware and fraud inspection to safeguard platform integrity, publisher reputations, and advertiser brand safety.",
      bullets: [
        "Forensiq Pre-Bid Traffic Filter",
        "Sherlock Active Malware Scanning",
        "TheMediaTrust Real-Time Verification",
        "Proactive Fraud Prevention Engine",
      ],
      highlight: "Sherlock & MediaTrust Verified",
      isDark: true,
      cardStyle: {
        background: "linear-gradient(165deg, #1C1410 0%, #150E06 100%)",
        borderColor: "#3D2612",
        borderRadius: "2.2rem 0.8rem 2.2rem 0.8rem",
        accent: "#F59E0B",
        accentBg: "#261A0C",
      },
    },
  ];

  return (
    <section
      id="servicesID"
      ref={ref}
      className="relative py-20 md:py-32 overflow-hidden border-t border-[#F5DFC5]"
      style={{
        background:
          "radial-gradient(90% 70% at 10% 10%, #FFF4E5 0%, transparent 55%), radial-gradient(90% 70% at 90% 90%, #FEF3E5 0%, transparent 55%), #FFFBF5",
      }}
    >
      {/* Background ambient blobs */}
      <div
        aria-hidden="true"
        className="absolute -top-24 -left-20 w-[480px] h-[480px] opacity-[0.16] blur-[110px] pointer-events-none"
        style={{ background: "radial-gradient(circle, #F59E0B, transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="absolute bottom-0 right-[-10%] w-[520px] h-[420px] opacity-[0.12] blur-[110px] pointer-events-none"
        style={{ background: "radial-gradient(circle, #D97706, transparent 70%)" }}
      />

      {/* MATCHING ALL OTHER SECTIONS: container mx-auto px-4 sm:px-6 lg:px-12 max-w-7xl */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-7xl relative z-10">
        {/* ================= SECTION HEADER ================= */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20 max-w-3xl mx-auto"
        >
          <div className="flex items-center w-full gap-4 mb-5">
            <span
              className="hidden sm:inline-block h-[2px] flex-1"
              style={{
                background: "linear-gradient(to left, #EA580C, transparent)",
              }}
            />
            <span className="text-xs sm:text-sm font-bold tracking-wide text-[#92400E] whitespace-nowrap">
              Cross-Channel Ad Network
            </span>
            <span
              className="hidden sm:inline-block h-[2px] flex-1"
              style={{
                background: "linear-gradient(to right, #EA580C, transparent)",
              }}
            />
          </div>

          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1C1410] tracking-tight leading-[1.14] mb-5"
            style={{ fontFamily: "'Fraunces', ui-serif, Georgia, serif" }}
          >
            Myad360{" "}
            <span className="relative inline-block whitespace-nowrap">
              <span
                className="bg-clip-text text-transparent bg-gradient-to-r from-[#D97706] via-[#E8882D] to-[#EA580C]"
                style={{ fontStyle: "italic", fontWeight: 500 }}
              >
                Innovative
              </span>
              <svg
                className="absolute left-0 -bottom-1.5 w-full"
                height="9"
                viewBox="0 0 200 9"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path
                  d="M2,6 C40,1 70,8 100,4 C130,0 160,7 198,3"
                  fill="none"
                  stroke="#D97706"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </span>{" "}
            Services
          </h2>

          <p className="text-[#4D3E30] text-sm sm:text-base leading-relaxed font-normal">
            Cross-channel monetization across Display, Mobile and Video. Combining premier first-look publisher access, innovative high-impact ad formats, and Forensiq pre-bidded fraud protection.
          </p>

          {/* Hand-crafted pill bar spanning header width */}
          <div className="mt-8 pt-6 border-t border-[#F5E8D5] flex flex-wrap items-center justify-center gap-3 text-xs sm:text-sm font-bold text-[#1C1410]">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-[#F0DCC8] shadow-sm">
              <FaRocket className="text-[#D97706]" /> NET 50 Terms
            </span>
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-[#F0DCC8] shadow-sm">
              <FaLayerGroup className="text-[#E8882D]" /> Skin / Gutter / Footers
            </span>
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-[#F0DCC8] shadow-sm">
              <FaLock className="text-[#F59E0B]" /> Sherlock &amp; MediaTrust Scanned
            </span>
          </div>
        </motion.div>

        {/* ================= 3-COLUMN BENTO GRID (FULL MAX-W-7XL) ================= */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-8 mb-14">
          {services.map((s, i) => {
            const isDark = s.isDark;
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 35 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{
                  duration: 0.6,
                  delay: 0.15 + i * 0.15,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={prefersReducedMotion ? {} : { y: -7 }}
                onMouseEnter={() => {}}
                onMouseLeave={() => {}}
                className={`group relative p-7 sm:p-8 border shadow-[0_15px_45px_-20px_rgba(28,20,16,0.12)] transition-all duration-400 flex flex-col justify-between overflow-hidden ${
                  isDark ? "text-white" : "text-[#1C1410]"
                }`}
                style={{
                  background: s.cardStyle.background,
                  borderColor: s.cardStyle.borderColor,
                  borderRadius: s.cardStyle.borderRadius,
                }}
              >
                {/* Accent top stripe */}
                <div
                  className="absolute top-0 left-8 right-8 h-[3px] rounded-b-full transition-all duration-300"
                  style={{ background: s.cardStyle.accent }}
                />

                <div>
                  {/* Badge & Icon row */}
                  <div className="flex items-center justify-between gap-3 mb-6">
                    <div
                      className="w-13 h-13 sm:w-14 sm:h-14 flex items-center justify-center text-xl shadow-sm transition-transform duration-500 group-hover:scale-105"
                      style={{
                        background: s.cardStyle.accentBg,
                        color: s.cardStyle.accent,
                        borderRadius: "45% 55% 60% 40% / 55% 45% 55% 45%",
                      }}
                    >
                      {s.icon}
                    </div>

                    <span
                      className={`text-[11px] font-extrabold px-3 py-1 rounded-full ${
                        isDark
                          ? "bg-[#261A0C] text-[#F59E0B] border border-[#3D2612]"
                          : "bg-white text-[#92400E] border border-[#F0DCC8] shadow-sm"
                      }`}
                    >
                      {s.badge}
                    </span>
                  </div>

                  {/* Titles */}
                  <span
                    className={`text-[11px] font-extrabold uppercase tracking-wider block mb-1 ${
                      isDark ? "text-[#F59E0B]" : "text-[#92400E]"
                    }`}
                  >
                    {s.tag}
                  </span>
                  <h3
                    className="text-xl sm:text-2xl font-bold mb-2 leading-tight"
                    style={{ fontFamily: "'Fraunces', serif" }}
                  >
                    {s.title}
                  </h3>
                  <p
                    className={`text-xs font-semibold mb-4 ${
                      isDark ? "text-[#A08E7A]" : "text-[#8A7560]"
                    }`}
                  >
                    {s.subtitle}
                  </p>

                  <p
                    className={`text-xs sm:text-sm leading-relaxed mb-6 font-normal ${
                      isDark ? "text-[#D4C4B0]" : "text-[#4D3E30]"
                    }`}
                  >
                    {s.description}
                  </p>

                  {/* Bullet features */}
                  <ul className="space-y-2.5 mb-6">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className={`flex items-center gap-2 text-xs font-semibold ${
                          isDark ? "text-[#D4C4B0]" : "text-[#3D2E22]"
                        }`}
                      >
                        <FaCheck
                          className="text-xs flex-shrink-0"
                          style={{ color: s.cardStyle.accent }}
                        />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Footer Highlight */}
                <div
                  className={`pt-4 border-t flex items-center justify-between text-xs font-bold ${
                    isDark ? "border-[#261A0C] text-[#F59E0B]" : "border-[#F5E8D5] text-[#D97706]"
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <HiCheckBadge className="text-sm" />
                    {s.highlight}
                  </span>
                  <FaArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* ================= FULL MAX-W-7XL BOTTOM RIBBON CTA ================= */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="relative"
        >
          <div
            className="bg-white border border-[#F0DCC8] shadow-[0_20px_50px_-20px_rgba(28,20,16,0.1)] p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 overflow-hidden"
            style={{ borderRadius: "1.8rem 0.8rem 1.8rem 0.8rem" }}
          >
            <div className="flex items-center gap-4 text-left">
              <div
                className="w-12 h-12 rounded-full bg-[#FEF3E5] text-[#D97706] flex items-center justify-center text-lg flex-shrink-0 shadow-sm"
              >
                <HiSparkles />
              </div>
              <div>
                <h4
                  className="text-base sm:text-lg font-bold text-[#1C1410]"
                  style={{ fontFamily: "'Fraunces', serif" }}
                >
                  Ready to optimize your cross-channel inventory?
                </h4>
                <p className="text-xs sm:text-sm text-[#4D3E30] font-normal">
                  Connect with our programmatic monetization team today for custom ad formats and zero-friction onboarding.
                </p>
              </div>
            </div>

            <a
              href="#contactID"
              className="group relative overflow-hidden inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-[#D97706] to-[#B45309] hover:from-[#7C3A0E] hover:to-[#D97706] text-white font-bold px-7 py-3.5 shadow-[0_16px_34px_-14px_rgba(217,119,6,0.5)] transition-all duration-300 text-xs sm:text-sm whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#D97706]"
              style={{ borderRadius: "1.5rem 0.5rem 1.5rem 0.5rem" }}
            >
              <span className="relative z-10 flex items-center gap-2">
                Partner With Us
                <FaArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </span>
              <span
                aria-hidden="true"
                className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12"
              />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Card;